import http from "./httpService"
import { AppConfiguration } from "@lib/appconst"

const CONFIG_FETCH_TIMEOUT_MS = 8_000
const CONFIG_FETCH_RETRIES = 3
const CONFIG_RETRY_DELAY_MS = 500

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const fetchConfigurationJson = async (): Promise<any> => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), CONFIG_FETCH_TIMEOUT_MS)

  try {
    const res = await fetch(`/configuration.json?t=${Date.now()}`, {
      cache: "no-store",
      signal: controller.signal,
    })
    if (!res.ok) {
      throw new Error(`Failed to load configuration.json (${res.status})`)
    }
    return await res.json()
  } finally {
    window.clearTimeout(timeoutId)
  }
}

class AppDataService {
  public async getAppConfiguration(): Promise<any> {
    let lastError: unknown

    for (let attempt = 1; attempt <= CONFIG_FETCH_RETRIES; attempt++) {
      try {
        const data = await fetchConfigurationJson()

        AppConfiguration.remoteServiceBaseUrl = data.remoteServiceBaseUrl
        AppConfiguration.appBaseUrl = data.appBaseUrl
        AppConfiguration.appLayoutConfig = data.appLayoutConfig
        AppConfiguration.googleMapKey = data.googleMapKey
        http.defaults.baseURL = data.remoteServiceBaseUrl
        return
      } catch (error) {
        lastError = error
        if (attempt < CONFIG_FETCH_RETRIES) {
          await sleep(CONFIG_RETRY_DELAY_MS * attempt)
        }
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error("Failed to load configuration.json")
  }

  public async getCountries(params: any): Promise<any> {
    const res = await http.get("api/services/app/Category/GetListCountry", {
      ...params,
    })
    const { result } = res.data
    // return (result || []).map((item) => {
    //   item.phoneCodeName = `${item.countryCode} (+${item.phoneCode})`;
    //   item.name = item.countryName;
    //   item.label = item.countryName;
    //   item.value = item.id;
    //   item.isLeaf = false; // for Cascader
    //   return item;
    // });
    return result
  }

  public async getProvinces(countryId: any): Promise<any> {
    const res = await http.get(`api/services/app/Category/GetListProvince`, {
      params: { countryId },
    })
    const { result } = res.data
    return (result || []).map((item) => {
      item.name = item.provinceName
      item.label = item.provinceName
      item.value = item.id
      return item
    })
  }

  public async getDistricts(provinceId): Promise<any> {
    const res = await http.get(`api/services/app/Category/GetListDistrict`, {
      params: { provinceId },
    })
    const { result } = res.data
    return (result || []).map((item) => {
      item.name = item.districtName
      item.label = item.districtName
      item.value = item.id
      return item
    })
  }
  public async getProjectProvinces(countryId: any): Promise<any> {
    const res = await http.get(
      `api/services/app/Category/GetListProjectProvince`,
      {
        params: { countryId },
      }
    )
    const { result } = res.data
    return (result || []).filter((item) => {
      if (item !== null) {
        item.name = item.provinceName
        item.label = item.provinceName
        item.value = item.id
      } else {
        return
      }
      return item
    })
  }

  public async getProjectDistricts(provinceId): Promise<any> {
    const res = await http.get(
      `api/services/app/Category/GetListProjectDistrict`,
      {
        params: { provinceId },
      }
    )
    const { result } = res.data
    return (result || []).filter((item) => {
      if (item !== null) {
        item.name = item?.districtName
        item.label = item?.districtName
        item.value = item?.id
      } else {
        return
      }
      return item
    })
  }
  public async getWards(params: any): Promise<any> {
    const res = await http.get("api/services/app/Category/GetListWards", {
      params,
    })
    const { result } = res.data
    return (result || []).map((item) => {
      item.name = item.wardName
      item.label = item.wardName
      item.value = item.id
      return item
    })
  }

  public async getDepartments(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/OrganizationUnit/GetOrganizationUnits",
      {
        ...params,
      }
    )
    const { result } = res.data
    return (result.items || []).map((item) => {
      item.name = item.displayName
      item.label = item.displayName
      item.value = item.id
      return item
    })
  }

  public async getDocumentType(params: any): Promise<any> {
    const res = await http.get(
      "api/services/app/Category/GetListDocumentType",
      {
        params,
      }
    )
    const { result } = res.data
    return result || []
  }

  // Project

  public async getClients(keyword): Promise<any> {
    const res = await http.get("api/services/app/Company/GetAll", {
      params: { keyword },
    })
    const result = (res.data.result?.items || []).map((item) => {
      return { id: item.id, label: item.businessName }
    })
    return result
  }
  public async getContacts(keyword): Promise<any> {
    const res = await http.get("api/services/app/Contact/GetAll", {
      params: { keyword },
    })
    const result = (res.data.result?.items || []).map((item) => {
      return { id: item.id, label: item.contactName }
    })
    return result
  }

  public async getInquirySourceAndStatus(): Promise<any> {
    const res = await http.get(
      "api/services/app/Category/GetListInquiryCategory"
    )
    const result = res.data.result || []
    return result
  }

  //PMH
  //Unit
  public async GetListUnitStatus(params: any): Promise<any> {
    const res = await http.get("/api/services/app/Category/GetListUnitStatus", {
      params,
    })
    const { result } = res.data

    return result
  }
  public async GetListUnitType(): Promise<any> {
    const res = await http.get("/api/services/app/Category/GetListUnitType", {})
    const { result } = res.data

    return result
  }

  public async GetListView(): Promise<any> {
    const res = await http.get("/api/services/app/Category/GetListView", {})
    const { result } = res.data

    return result
  }
  public async GetListCart(params?): Promise<any> {
    const res = await http.get("/api/services/app/Cart/GetList", { params })
    const { result } = res.data

    return result
  }
  public async GetListFacing() {
    const res = await http.get("/api/services/app/Category/GetListFacing", {})
    const { result } = res.data

    return result
  }
  public async GetListLeadSource(params: any): Promise<any> {
    const res = await http.get("api/services/app/Category/GetListLeadSource", {
      params,
    })
    const { result } = res.data

    return result
  }
  //Project

  // master data
  public async GetListTownShip(params?: any): Promise<any> {
    const res = await http.get("/api/services/app/Township/GetList", { params })
    const { result } = res.data

    return result
  }
  public async getListProject(): Promise<any> {
    const res = await http.get("/api/services/app/Project/GetMyAssignedProjects", {})
    const { result } = res.data

    return result
  }
  public async getListUnit(params?: any): Promise<any> {
    const res = await http.get("/api/services/app/Unit/GetList", { params })
    const { result } = res.data

    return result
  }
  public async GetListTower(): Promise<any> {
    const res = await http.get("/api/services/app/Tower/GetList", {})
    const { result } = res.data

    return result
  }
  public async GetListZone(): Promise<any> {
    const res = await http.get("/api/services/app/Zone/GetList", {})
    const { result } = res.data

    return result
  }
  public async GetListFloor(): Promise<any> {
    const res = await http.get("/api/services/app/Floor/GetList", {})
    const { result } = res.data

    return result
  }
  public async GetListUnitFacility(): Promise<any> {
    const res = await http.get("/api/services/app/UnitFacilities/GetList", {})
    const { result } = res.data

    return result
  }
  public async GetListUnitDesign(): Promise<any> {
    const res = await http.get("/api/services/app/UnitDesign/GetList", {})
    const { result } = res.data
    return result
  }
  public async GetListProjectFacility(): Promise<any> {
    const res = await http.get("/api/services/app/ProjectFacility/GetList", {})
    const { result } = res.data

    return result
  }
  public async getListEmployee(params?: any): Promise<any> {
    const res = await http.get("api/services/app/Employees/GetList", {
      params,
    })
    const result = res.data.result
    return result
  }
  public async GetListAgencyMaster(params?): Promise<any> {
    const res = await http.get(
      "/api/services/app/AgencyMasters/GetListAgencyMaster",
      { params }
    )
    const { result } = res.data

    return result
  }
  public async getListCartTemplate(params?: any): Promise<any> {
    const res = await http.get(
      "api/services/app/Notification/GetListCartTemplate",
      {
        params,
      }
    )
    const result = res.data.result?.map((i) => {
      return {
        id: i.id,
        label: i.subject,
      }
    })
    return result
  }
  // CRM

  public async getListPotentialChannel(): Promise<any> {
    const res = await http.get(
      "api/services/app/Category/GetListPotentialChannel"
    )
    const result = res.data.result || []
    return result
  }
  public async getListBookingStatus(params?): Promise<any> {
    const res = await http.get(
      "/api/services/app/Category/GetListBookingStatus",
      { params }
    )
    const { result } = res.data

    return result
  }
  public async getListContractType(params?): Promise<any> {
    const res = await http.get("/api/services/app/ContractType/GetList", {
      params,
    })
    const { result } = res.data

    return result
  }

  public async getListCompany(params?): Promise<any> {
    const res = await http.get("/api/services/app/Company/GetList", {
      params,
    })
    const { result } = res.data

    return result
  }
  public async getListPaymentProgress(params?): Promise<any> {
    const res = await http.get("/api/services/app/PaymentProgress/GetList", {
      params,
    })
    const { result } = res.data

    return result
  }
}
export default new AppDataService()
